import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { type RootState } from "../store";
import type {
  CouponType,
  PriceTierType,
  PriceTierVersion,
} from "@/types/trustpilotPricing";
import { filterSelectedCompaniesWithReviewsTrustPilot } from "@/utils/orderHelper";
import { TrustPilotCartShareData } from "@/types/cartShareTypes";
import {
  isPersistentCartUnavailableError,
  PersistedCartDataTrustPilot,
  PersistedCartPayloadTrustPilot,
  persistentCartApi,
} from "@/api/persistentCartApi";
import { CART_SHARE_UUID } from "@/utils/constants/cartShare";
import reviewTrustPilotApi, {
  SearchBusinessTrustPilotResultType,
  TrustPilotBusinessType,
  TrustPilotOrderDataPayload,
  type TrustPilotPersistedBusinessPayload,
  TrustPilotReviewType,
} from "@/api/reviewTrustPilotApi";
import {
  filterReviewsTrustPilot,
  handleFilteredReviewsTrustPilot,
} from "@/utils/hooks/useSelectReviewsTrustPilot";
import { reportToFlare } from "@/utils/reportToFlare";
import {
  createSingleFlightOrderGuard,
  isDuplicateOrderSubmission,
  ORDER_SUBMISSION_IN_PROGRESS,
} from "@/utils/analytics/orderSubmissionGuard";
import { getPurchaseIdempotencyKey } from "@/utils/analytics/purchaseIdempotency";
import { LIVE_CHAT_ID } from "@/utils/constants/storageKeys";
import { FUNNEL_TRUST_PILOT } from "@/types/funnelTypes";
import { OptionsType } from "@components/Input/InputSelectCustomDropdown";
import axios, { type AxiosError } from "axios";
import { type AnyAction, type Dispatch } from "redux";
import { getTrustPilotBusinessDomain } from "@/utils/trustPilotBusiness";
import {
  createReviewPrefetchKey,
  getOrCreateReviewPrefetch,
} from "@/utils/reviewPrefetchCache";
import { checkoutConfirmedFromUser } from "@/utils/checkoutUserDraft";
import { REPUTECH_DOMAIN } from "@/utils/constants/brand";

const createOrderGuard = createSingleFlightOrderGuard();

export const resetReviewStateTrustPilot = createAction(
  "TrustPilotReviewSlice/resetReviewState",
);

export type FetchGetReviewsArgs = {
  companyId?: string;
  cartShareUuid?: string | null;
};

type TrustPilotUserFormData = {
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  salutation: string;
  firma: string;
  location: string;
  plz: string;
  street: string;
  newsletterOptIn?: boolean;
  checkout_confirmed?: boolean;
};

type TrustPilotUserFormField = keyof TrustPilotUserFormData;

const TRUSTPILOT_GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";
const TRUSTPILOT_SEARCH_FAILED_MESSAGE =
  "We could not complete your search. Try again or search for the official name of your Trustpilot profile.";
const TRUSTPILOT_INVALID_CART_SHARE_MESSAGE =
  "This shared review cart is invalid or has expired.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

type TrustPilotCartShareReview = TrustPilotReviewType | string;

type TrustPilotCartShareBusinessPayloadWithIds = {
  business_id: string;
  reviews?: TrustPilotCartShareReview[];
};

const isTrustPilotCartShareBusinessPayload = (
  value: unknown,
): value is TrustPilotCartShareBusinessPayloadWithIds =>
  isRecord(value) &&
  typeof value.business_id === "string" &&
  (value.reviews === undefined || Array.isArray(value.reviews));

const getPersistedTrustPilotBusiness = (
  payload?: PersistedCartDataTrustPilot,
): TrustPilotPersistedBusinessPayload | undefined => {
  if (!payload) {
    return undefined;
  }

  const { businesses } = payload;

  return businesses[0];
};

const getAxiosErrorMessage = (error: AxiosError) => {
  const responseData = error.response?.data;

  return isRecord(responseData) && typeof responseData.message === "string"
    ? responseData.message
    : undefined;
};

const readStoredTrustPilotCartUuid = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedData = window.localStorage.getItem(CART_SHARE_UUID);
    const uuidData: unknown = storedData ? JSON.parse(storedData) : null;

    return isRecord(uuidData) && typeof uuidData.uuid === "string"
      ? uuidData.uuid
      : null;
  } catch (error) {
    reportToFlare(error, "trustpilot_persistent_cart_uuid_read");
    return null;
  }
};

const dedupeTrustPilotReviewsById = (
  reviews: TrustPilotReviewType[],
): TrustPilotReviewType[] => {
  const seenReviewIds = new Set<string>();

  return reviews.filter((review) => {
    if (!review.id) {
      // Reviews without stable backend ids cannot be safely deduped by id;
      // keep them instead of dropping user-visible reviews.
      reportToFlare(
        new Error("Trustpilot review is missing a stable id"),
        "trustpilot_review_missing_id",
      );
      return true;
    }

    if (seenReviewIds.has(review.id)) {
      return false;
    }

    seenReviewIds.add(review.id);
    return true;
  });
};
const countTrustPilotReviewsByRating = (reviews: TrustPilotReviewType[]) => {
  const counts = { 1: 0, 2: 0, 3: 0 };
  reviews.forEach((review) => {
    const rating = Math.floor(Number(review.rating));
    if (rating >= 1 && rating <= 3) counts[rating as 1 | 2 | 3] += 1;
  });
  return counts;
};

// Tab-scoped single-flight guard: a fast double tap must not POST two orders.

export const fetchCreateReviewTrustPilot = createAsyncThunk(
  "TrustPilotReviewSlice/fetchCreateReviewTrustPilot",
  async (_, { rejectWithValue, getState, signal, dispatch, requestId }) => {
    if (!createOrderGuard.tryAcquire(requestId)) {
      return rejectWithValue(ORDER_SUBMISSION_IN_PROGRESS);
    }

    try {
      return await submitCreateReviewTrustPilot({
        rejectWithValue,
        getState,
        signal,
        dispatch,
      });
    } finally {
      createOrderGuard.release(requestId);
    }
  },
);

const submitCreateReviewTrustPilot = async ({
  rejectWithValue,
  getState,
  signal,
  dispatch,
}: {
  rejectWithValue: (value: unknown) => unknown;
  getState: () => unknown;
  signal: AbortSignal | undefined;
  dispatch: Dispatch<AnyAction>;
}) => {
  try {
    const store = getState() as RootState;
    const name = store.reviewTrustPilotReducer.data.firstName;
    const lastName = store.reviewTrustPilotReducer.data.surname;
    const email = store.reviewTrustPilotReducer.data.email;
    const phone = store.reviewTrustPilotReducer.data.phone;
    const coupon = store.reviewTrustPilotReducer.coupon;
    const couponData = store.reviewTrustPilotReducer.couponData;
    const selectedCompany = store.reviewTrustPilotReducer.selectedCompany;

    const userData = store.reviewTrustPilotReducer.data;
    const checkoutConfirmed = Boolean(userData.checkout_confirmed);

    if (!selectedCompany?.domain) {
      return rejectWithValue(
        "Select a Trustpilot profile first.",
      );
    }

    const selectedReviews = selectedCompany.userSelectedReviews ?? [];
    const reviewIds = selectedReviews
      .map((review) => review.id)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
    if (
      reviewIds.length === 0 ||
      reviewIds.length !== selectedReviews.length
    ) {
      return rejectWithValue(
        "Select at least one valid Trustpilot review.",
      );
    }

    const data: TrustPilotOrderDataPayload = {
      frontend_domain: REPUTECH_DOMAIN,
      business_id: selectedCompany.domain,
      review_ids: reviewIds,
      user: {
        name: name,
        lastName: lastName,
        email: email,
        phone: phone,
        firma: userData.firma,
        salutation: userData.salutation,
        plz: userData.plz,
        location: userData.location,
        street: userData.street,
        coupon_used: !couponData?.error ? coupon : null,
        // Keep the redesigned checkout confirmation in the existing API fields.
        newsletterOptIn: Boolean(userData.newsletterOptIn),
        businessCustomerConfirmed: checkoutConfirmed,
        termsAccepted: checkoutConfirmed,
        reviewSubmissionAuthorized: checkoutConfirmed,
        successBasedBillingAccepted: checkoutConfirmed,
      },
      idempotency_key: getPurchaseIdempotencyKey(),
    };

    try {
      const liveChatId =
        typeof window !== "undefined"
          ? window.localStorage.getItem(LIVE_CHAT_ID)
          : null;

      if (liveChatId) {
        data.live_chat_id = liveChatId;
      }
    } catch (error) {
      reportToFlare(error, "trustpilot_live_chat_id_read");
    }

    // One stable anchor per checkout attempt, same contract as the Google
    // funnel: reloads and payment returns resend the same key so the backend
    // can collapse duplicate creates instead of producing a second order.
    try {
      data.idempotency_key = getPurchaseIdempotencyKey();
    } catch (error) {
      reportToFlare(error, "trustpilot_idempotency_key");
    }

    const created = await reviewTrustPilotApi.create(data, signal);

    return created;
  } catch (error) {
    reportToFlare(error, "trustpilot_create_review");

    if (axios.isAxiosError(error) && error.response?.status === 422) {
      return rejectWithValue(getAxiosErrorMessage(error));
    }

    return rejectWithValue(TRUSTPILOT_GENERIC_ERROR_MESSAGE);
  }
};

export const fetchGetCompanyTrustPilot = createAsyncThunk(
  "TrustPilotReviewSlice/fetchGetCompanyTrustPilot",
  async (_, { rejectWithValue, getState }) => {
    try {
      const store = getState() as RootState;
      const query = store.reviewTrustPilotReducer.searchText.trim();
      const limitCompany = store.reviewTrustPilotReducer.limitCompany;
      const companyCursor = store.reviewTrustPilotReducer.companyCursor;
      const response = await reviewTrustPilotApi.get({
        query: query,
        limit: limitCompany,
        ...(companyCursor && { cursor: companyCursor }),
      });

      // success:false is a failed lookup, not "no matching profile".
      if (response.success === false && response.businessUnits.length === 0) {
        return rejectWithValue(TRUSTPILOT_SEARCH_FAILED_MESSAGE);
      }

      // Handle both response formats: plain array or object with results
      const data = response;
      const countAllReview = {} as { [key: string]: number };
      data.businessUnits?.forEach((business) => {
        countAllReview[business.businessUnitId] = business.numberOfReviews;
      });

      return {
        data,
        countAllReview,
      };
    } catch (error) {
      reportToFlare(error, "trustpilot_get_company");
      return rejectWithValue(TRUSTPILOT_GENERIC_ERROR_MESSAGE);
    }
  },
);

export const prefetchBusinessReviewsTrustPilot = createAsyncThunk(
  "TrustPilotReviewSlice/prefetchBusinessReviewsTrustPilot",
  async (companyId: string, { rejectWithValue }) => {
    if (!companyId) return rejectWithValue("Company id is required");

    try {
      await getOrCreateReviewPrefetch(
        createReviewPrefetchKey(companyId),
        () =>
          reviewTrustPilotApi.getReviews({
            companyId,
            page: 1,
            sort: "relevance",
          }),
      );
    } catch {
      return rejectWithValue("Unable to prefetch reviews");
    }
  },
);

export const fetchGetReviewsTrustPilot = createAsyncThunk(
  "TrustPilotReviewSlice/fetchGetReviewsTrustPilot",
  async (
    { companyId, cartShareUuid }: FetchGetReviewsArgs,
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const uuid = readStoredTrustPilotCartUuid();

      const store = getState() as RootState;
      let review_share: TrustPilotCartShareReview[] | undefined = undefined;
      let coupon_code: string | undefined;
      let company_id_cart_share = "";
      let isCustomerNurturingCartShare = false;
      if (cartShareUuid != null && cartShareUuid.length > 0) {
        const review = await reviewTrustPilotApi.getCartShareData(cartShareUuid);
        const cartSharePayload = review?.data?.payload;
        isCustomerNurturingCartShare =
          isRecord(cartSharePayload) &&
          cartSharePayload.source === "customer_nurturing";

        if (!cartSharePayload) {
          reportToFlare(
            new Error("Trustpilot cart-share response payload is missing"),
            "trustpilot_cart_share_missing_payload",
            { cartShareUuid },
          );
          return rejectWithValue(TRUSTPILOT_INVALID_CART_SHARE_MESSAGE);
        }

        coupon_code = cartSharePayload.coupon_code;
        const businesses = cartSharePayload.businesses;

        if (!Array.isArray(businesses)) {
          reportToFlare(
            new Error("Trustpilot cart-share payload businesses is malformed"),
            "trustpilot_cart_share_malformed_businesses",
            { cartShareUuid },
          );
          return rejectWithValue(TRUSTPILOT_INVALID_CART_SHARE_MESSAGE);
        } else if (businesses.length === 0) {
          reportToFlare(
            new Error(
              "Trustpilot cart-share payload did not include a business",
            ),
            "trustpilot_cart_share_missing_business",
            { cartShareUuid },
          );
          return rejectWithValue(TRUSTPILOT_INVALID_CART_SHARE_MESSAGE);
        } else {
          if (businesses.length > 1) {
            reportToFlare(
              new Error(
                "Trustpilot cart-share payload included multiple businesses",
              ),
              "trustpilot_cart_share_multiple_businesses",
              {
                cartShareUuid,
                businessCount: businesses.length,
              },
            );
          }

          const [cartShareBusiness] = businesses;

          if (!isTrustPilotCartShareBusinessPayload(cartShareBusiness)) {
            reportToFlare(
              new Error("Trustpilot cart-share payload business is malformed"),
              "trustpilot_cart_share_malformed_business",
              { cartShareUuid },
            );
            return rejectWithValue(TRUSTPILOT_INVALID_CART_SHARE_MESSAGE);
          } else {
            review_share = cartShareBusiness.reviews;
            company_id_cart_share = cartShareBusiness.business_id;
          }
        }
      }

      const resolvedCompanyId = companyId || company_id_cart_share;
      const reviewsResponse = await getOrCreateReviewPrefetch(
        createReviewPrefetchKey(resolvedCompanyId),
        () =>
          reviewTrustPilotApi.getReviews(
            {
              companyId: resolvedCompanyId,
              page: 1,
              sort: "relevance",
            },
          ),
      );

      const company = reviewsResponse.business;
      const fetchedReviews = dedupeTrustPilotReviewsById(
        reviewsResponse.reviews,
      );
      const cartShareBusinessMatches =
        company_id_cart_share.trim().toLowerCase() ===
        resolvedCompanyId.trim().toLowerCase();
      const matchingCartShareReviews = cartShareBusinessMatches
        ? (review_share ?? [])
        : [];
      const cartShareReviewIds = matchingCartShareReviews.filter(
        (review): review is string => typeof review === "string",
      );
      const fetchedReviewById =
        isCustomerNurturingCartShare && cartShareReviewIds.length > 0
          ? new Map(
              fetchedReviews.map((fetchedReview) => [
                fetchedReview.id,
                fetchedReview,
              ] as const),
            )
          : undefined;
      let pagination = reviewsResponse.pagination;
      let currentPage = Number(pagination.current_page) || 1;
      let totalPages = Number(pagination.total_pages) || currentPage;
      let cartShareReviewFetchFailed = false;

      if (fetchedReviewById) {
        try {
          while (
            currentPage < totalPages &&
            cartShareReviewIds.some((id) => !fetchedReviewById.has(id))
          ) {
            const nextPage = currentPage + 1;
            const nextPageResponse = await reviewTrustPilotApi.getReviews(
              {
                companyId: resolvedCompanyId,
                page: nextPage,
                sort: "relevance",
              },
            );
            const nextPageReviews = dedupeTrustPilotReviewsById(
              nextPageResponse.reviews,
            );

            for (const fetchedReview of nextPageReviews) {
              if (
                fetchedReview.id &&
                fetchedReviewById.has(fetchedReview.id)
              ) {
                continue;
              }
              if (fetchedReview.id) {
                fetchedReviewById.set(fetchedReview.id, fetchedReview);
              }
              fetchedReviews.push(fetchedReview);
            }

            pagination = nextPageResponse.pagination;
            currentPage = Math.max(
              Number(pagination.current_page) || nextPage,
              nextPage,
            );
            totalPages = Math.max(
              Number(pagination.total_pages) || currentPage,
              currentPage,
            );
          }
        } catch (error) {
          reportToFlare(error, "trustpilot_cart_share_review_page_fetch");
          cartShareReviewFetchFailed = true;
        }
      }

      const missingCartShareReviewIds = isCustomerNurturingCartShare
        ? cartShareReviewIds.filter((id) => !fetchedReviewById?.has(id))
        : [];
      const cartShareHasUnsupportedReviewIds =
        !isCustomerNurturingCartShare && cartShareReviewIds.length > 0;

      if (
        (isCustomerNurturingCartShare && !cartShareBusinessMatches) ||
        cartShareHasUnsupportedReviewIds ||
        cartShareReviewFetchFailed ||
        missingCartShareReviewIds.length > 0
      ) {
        reportToFlare(
          new Error("Trustpilot cart-share reviews could not be restored"),
          "trustpilot_cart_share_reviews_unavailable",
          {
            cartShareUuid,
            missingReviewCount: missingCartShareReviewIds.length,
          },
        );
        return {
          dataArray: fetchedReviews,
          fetchedCompany: company,
          selectedReviews: [],
          pagination,
          cartShareInvalid: true,
        };
      }

      const cartShareReviews = matchingCartShareReviews.flatMap(
        (shareReview): TrustPilotReviewType[] => {
          if (typeof shareReview !== "string") return [shareReview];

          const fetchedReview = fetchedReviewById?.get(shareReview);
          return fetchedReview ? [fetchedReview] : [];
        },
      );
      const couponBusinessId = companyId || company_id_cart_share;

      const cart_saved_data_get =
        uuid &&
        (await persistentCartApi.getCartData<PersistedCartDataTrustPilot>(
          uuid,
        ));
      const payload = cart_saved_data_get
        ? cart_saved_data_get.data?.payload
        : undefined;
      const persistedBusiness = getPersistedTrustPilotBusiness(payload);
      const persistedReviews =
        persistedBusiness?.domain?.trim().toLowerCase() ===
        resolvedCompanyId.trim().toLowerCase()
          ? (persistedBusiness.reviews ?? [])
          : [];
      const persistedUser = payload?.user;
      const persistedUserData: Partial<TrustPilotUserFormData> | undefined =
        persistedUser
          ? {
              firstName: persistedUser.firstName,
              surname: persistedUser.surname,
              email: persistedUser.email,
              phone: persistedUser.phone,
              salutation: persistedUser.salutation,
              firma: persistedUser.firma,
              location: persistedUser.location,
              plz: persistedUser.plz,
              street: persistedUser.street,
              checkout_confirmed: checkoutConfirmedFromUser(
                persistedUser as Record<string, unknown>,
              ),
            }
          : undefined;
      const persistedCouponCode = payload?.coupon_code;
      // To prevent triggering the persistentCart middleware, as the states in this phase are not complete yet
      if (persistedUserData) {
        dispatch(
          reviewTrustPilotActions.setUserDataBulkNonPersisting(
            persistedUserData,
          ),
        );
      }

      // Restored local carts intentionally keep their persisted coupon before
      // applying a coupon from a cart-share link.
      const couponCodeToCheck = persistedCouponCode || coupon_code;
      if (couponCodeToCheck) {
        const check_coupon = await reviewTrustPilotApi.checkCoupon(
          couponCodeToCheck,
          couponBusinessId ? [couponBusinessId] : [],
        );

        const couponDataToUse = check_coupon?.data?.data;
        if (couponDataToUse) {
          dispatch(
            reviewTrustPilotActions.setCouponSimplified({
              code: couponCodeToCheck,
              data: couponDataToUse,
            }),
          );
        }
      }

      const selectedReviews = dedupeTrustPilotReviewsById(
        isCustomerNurturingCartShare
          ? cartShareReviews
          : [...persistedReviews, ...cartShareReviews],
      );
      const selectedReviewIdsArray = selectedReviews
        .map((it) => it.id)
        .filter((id): id is string => !!id);

      let dataArray = fetchedReviews;
      if (selectedReviewIdsArray.length > 0) {
        const selectedReviewIdSet = new Set(selectedReviewIdsArray);
        dataArray = dataArray?.map((it): TrustPilotReviewType => {
          if (it?.id && selectedReviewIdSet.has(it.id)) {
            return { ...it, isChosenItem: true };
          }
          return it;
        });
      }

      return {
        dataArray,
        fetchedCompany: company,
        selectedReviews,
        pagination,
        cartShareInvalid: false,
      };
    } catch (e) {
      reportToFlare(e, "trustpilot_get_reviews");
      return rejectWithValue(TRUSTPILOT_GENERIC_ERROR_MESSAGE);
    }
  },
);

export const fetchGetReviewsMoreTrustPilot = createAsyncThunk(
  "TrustPilotReviewSlice/fetchGetReviewsMoreTrustPilot",
  async (companyId: string, { rejectWithValue, getState }) => {
    try {
      const state = (getState() as RootState).reviewTrustPilotReducer;
      const requestedCompanyId = companyId || state.selectedCompany.domain;
      const requestedPage = Number(state.pagination.current_page) + 1;
      const reviewsResponse = await reviewTrustPilotApi.getReviews(
        {
          companyId: requestedCompanyId,
          page: requestedPage,
          sort: "relevance",
        },
      );

      return {
        companyId: requestedCompanyId,
        requestedPage,
        reviews: reviewsResponse.reviews ?? [],
        pagination: reviewsResponse.pagination,
      };
    } catch (e) {
      reportToFlare(e, "trustpilot_get_more_reviews");
      return rejectWithValue(TRUSTPILOT_GENERIC_ERROR_MESSAGE);
    }
  },
  {
    condition: (companyId, { getState }) => {
      const state = (getState() as RootState).reviewTrustPilotReducer;
      const requestedCompanyId = companyId || state.selectedCompany.domain;
      return (
        Boolean(requestedCompanyId) &&
        requestedCompanyId === state.selectedCompany.domain &&
        !state.isLoading &&
        !state.fetchGetReviewsMoreTrustPilot &&
        state.hasMoreReviewPages &&
        Number(state.pagination.current_page) <
          Number(state.pagination.total_pages)
      );
    },
  },
);

export const fetchCheckCouponTrustPilot = createAsyncThunk(
  "TrustPilotReviewSlice/fetchCheckCouponTrustPilot",
  async (
    { coupon, businessIds }: { coupon: string; businessIds?: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await reviewTrustPilotApi.checkCoupon(
        coupon,
        businessIds ?? [],
      );

      return response?.data?.data;
    } catch (error) {
      reportToFlare(error, "trustpilot_check_coupon");
      return rejectWithValue("We could not apply this coupon. Check the code and try again.");
    }
  },
);

export const fetchPersistentCartApiTrustPilot = createAsyncThunk(
  "TrustPilotReviewSlice/fetchPersistentCartApiTrustPilot",
  async (
    { uuid, data }: { uuid: string; data: PersistedCartPayloadTrustPilot },
    { rejectWithValue, getState },
  ) => {
    try {
      const response = await persistentCartApi.patchCartData(uuid, data);
      return response;
    } catch (e) {
      if (!isPersistentCartUnavailableError(e)) {
        reportToFlare(e, "trustpilot_persist_cart");
      }

      return rejectWithValue(TRUSTPILOT_GENERIC_ERROR_MESSAGE);
    }
  },
);

const initialState = {
  searchText: "",
  data: {} as TrustPilotUserFormData,
  formsQuestion: {} as { [key: string]: string },
  isLoading: true as boolean | null,
  isLoadingFetchGetCompany: true as boolean | null,
  numberLastItem: 0 as number,
  isSendData: false,
  isSuccessRequest: null as boolean | null,
  items: [] as TrustPilotReviewType[],
  itemsDefault: [] as TrustPilotReviewType[],
  selectedItem: 0,
  disableForm: false as boolean,
  countReview: 0,
  company: [] as SearchBusinessTrustPilotResultType[],
  limit: 9,
  limitCompany: 5,
  page: 1,
  isOpenModal: false,
  countAllReview: {} as { [key: string]: number },
  addressCompany: "",
  selectedCompany: {} as TrustPilotBusinessType, // current selected company
  selectedCompanies: [] as TrustPilotBusinessType[],
  coupon: "",
  couponData: {} as CouponType,
  reviewShowTextAreaId: [] as string[],
  chosenCheckBoxRating: [] as number[],
  companyRatingFilters: {} as { [key: string]: number[] },
  priceItem: 0,
  error: "" as string | undefined | null,
  isOpenCart: false,
  reviewsCursor: {} as { [key: string]: string | undefined }, // Cursor for each company
  hasMoreReviews: {} as { [key: string]: boolean }, // Track if more reviews exist
  companyCursor: undefined as string | undefined, // Cursor for company search,
  priceTier: undefined as PriceTierType | undefined,
  version: undefined as PriceTierVersion | undefined,
  priceTierReviewCountLimit: -1 as number,
  selectedReviewFilter: undefined as OptionsType | undefined,
  isCartShareOpen: false,
  cartShare: undefined as TrustPilotCartShareData | undefined,
  countReviewByRating: {
    1: 0,
    2: 0,
    3: 0,
  },
  fetchGetReviewsMoreTrustPilot: false,
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_reviews: 1,
  },
  hasMoreReviewPages: false,
};

type ReviewTrustPilotState = typeof initialState;

const getSelectedCompany = (
  state: Pick<ReviewTrustPilotState, "selectedCompanies" | "selectedCompany">,
) => {
  return (
    state.selectedCompanies.find((item) =>
      state.selectedCompany.domain
        ? item.domain === state.selectedCompany.domain
        : item.id === state.selectedCompany.id,
    ) ?? state.selectedCompany
  );
};

const syncTrustPilotSelectedReviews = (
  state: ReviewTrustPilotState,
  reviews: TrustPilotReviewType[],
) => {
  const selectedCompany = state.selectedCompany;
  if (!selectedCompany?.domain) return;

  const nextCompany = {
    ...selectedCompany,
    userSelectedReviews: dedupeTrustPilotReviewsById(reviews),
  };
  const companyIndex = state.selectedCompanies.findIndex(
    (company) => company.domain === nextCompany.domain,
  );

  state.selectedCompany = nextCompany;
  if (companyIndex === -1) {
    state.selectedCompanies.push(nextCompany);
  } else {
    state.selectedCompanies[companyIndex] = nextCompany;
  }
};

const syncTrustPilotRatingCheckboxes = (state: ReviewTrustPilotState) => {
  const selectedCounts = countTrustPilotReviewsByRating(
    state.selectedCompany.userSelectedReviews ?? [],
  );
  state.chosenCheckBoxRating = ([1, 2, 3] as const).filter(
    (rating) =>
      selectedCounts[rating] > 0 &&
      selectedCounts[rating] === state.countReviewByRating[rating],
  );
};

const TrustPilotReviewSlice = createSlice({
  name: "TrustPilotReviewSlice",
  initialState: initialState,
  reducers: {
    toggleOpenCart: {
      reducer: (state, { payload }: { payload?: { visibility: boolean } }) => {
        if (payload?.visibility !== undefined) {
          state.isOpenCart = payload.visibility;
          return;
        }
        state.isOpenCart = !state.isOpenCart;
      },
      prepare: (payload?: { visibility: boolean }) => ({ payload }),
    },
    setPriceItem: (state, { payload }) => {
      state.priceItem = payload;
    },
    handleCompanySelection: (
      state,
      {
        payload,
      }: {
        payload: {
          company: SearchBusinessTrustPilotResultType;
          trackOptitagConversion?: boolean;
        };
      },
    ) => {
      const { company } = payload;

      state.selectedCompany = {
        id: 0,
        name: company.displayName || company.identifyingName,
        domain: getTrustPilotBusinessDomain(company),
        logo_url: company.logoUrl,
        rating: company.stars,
        trust_score: company.trustScore,
        review_count: company.numberOfReviews,
        one_star_reviews_count: 0,
        two_star_reviews_count: 0,
        three_star_reviews_count: 0,
        country: company.location?.country,
        website_url: company.contact?.website,
        source: "trustpilot_search",
      };
      state.selectedCompanies = [state.selectedCompany];
      state.hasMoreReviewPages = false;
    },
    setSelectedReviewFilter: (state, { payload }: { payload: OptionsType }) => {
      state.selectedReviewFilter = payload;
    },
    setChosenCheckBoxRating: (state, { payload = {} }) => {
      const number = payload.number;
      if (state.chosenCheckBoxRating?.includes(number)) {
        state.chosenCheckBoxRating = state.chosenCheckBoxRating?.filter(
          (it) => it !== number,
        );
      } else {
        state.chosenCheckBoxRating = [...state.chosenCheckBoxRating, number];
      }
    },
    setChoseRatingItem: (state, { payload }) => {
      const { reviewArray, rating } = payload;
      const isRatingInChosenBox = state.chosenCheckBoxRating.includes(rating);
      const reviewIds = new Set(reviewArray.filter(Boolean));

      state.items = state.items.map((review) =>
        reviewIds.has(review.id)
          ? { ...review, isChosenItem: !isRatingInChosenBox }
          : review,
      );
      syncTrustPilotSelectedReviews(
        state,
        handleFilteredReviewsTrustPilot(
          state.items,
          state.selectedCompany.userSelectedReviews,
        ),
      );
      syncTrustPilotRatingCheckboxes(state);
    },
    selectReviewsForTargetRating: (
      state,
      { payload }: { payload: Record<1 | 2 | 3, number> },
    ) => {
      const remaining = {
        1: Math.max(0, Math.floor(Number(payload[1]) || 0)),
        2: Math.max(0, Math.floor(Number(payload[2]) || 0)),
        3: Math.max(0, Math.floor(Number(payload[3]) || 0)),
      };

      state.items = state.items.map((review) => {
        const rating = Math.floor(Number(review.rating));
        if (rating < 1 || rating > 3) return review;

        const isChosenItem =
          Boolean(review.id) && remaining[rating as 1 | 2 | 3] > 0;
        if (isChosenItem) remaining[rating as 1 | 2 | 3] -= 1;
        return { ...review, isChosenItem };
      });

      const loadedReviewIds = new Set(
        state.items.map((item) => item.id).filter(Boolean),
      );
      const selectedOutsideTargetRatings = (
        state.selectedCompany.userSelectedReviews ?? []
      ).filter((review) => {
        const rating = Math.floor(Number(review.rating));
        const isTargetRating = rating >= 1 && rating <= 3;
        return !isTargetRating && !loadedReviewIds.has(review.id);
      });
      syncTrustPilotSelectedReviews(state, [
        ...state.items.filter((review) => review.isChosenItem),
        ...selectedOutsideTargetRatings,
      ]);
      syncTrustPilotRatingCheckboxes(state);
    },
    setQuickRatingSelection: (
      state,
      { payload }: { payload: { rating: 1 | 2 | 3; count: number } },
    ) => {
      const { rating, count } = payload;
      const selectedIds = new Set(
        state.items
          .filter((review) => Math.floor(Number(review.rating)) === rating)
          .slice(0, Math.max(0, Math.floor(Number(count) || 0)))
          .map((review) => review.id)
          .filter(Boolean),
      );
      state.items = state.items.map((review) =>
        Math.floor(Number(review.rating)) === rating
          ? {
              ...review,
              isChosenItem: Boolean(review.id) && selectedIds.has(review.id),
            }
          : review,
      );

      const selectedOtherRatings = (
        state.selectedCompany.userSelectedReviews ?? []
      ).filter((review) => Math.floor(Number(review.rating)) !== rating);
      syncTrustPilotSelectedReviews(
        state,
        handleFilteredReviewsTrustPilot(state.items, selectedOtherRatings),
      );
      syncTrustPilotRatingCheckboxes(state);
    },
    setIsLoading: (state, { payload }) => {
      state.isLoading = payload;
    },
    setShowTextArea: (state, { payload }) => {
      state.reviewShowTextAreaId = [...state.reviewShowTextAreaId, payload];
    },
    setChoseAllItem: (state) => {
      state.items = state.items?.map((item) => {
        return { ...item, isChosenItem: true };
      });
    },
    clearAllSelectedItems: (state) => {
      const selectedCompany = getSelectedCompany(state);

      state.items = state.items?.map((item) => {
        return { ...item, isChosenItem: false };
      });

      selectedCompany.userSelectedReviews = [];
    },
    setCoupon: (state, { payload }) => {
      state.coupon = payload;
    },
    setCouponData: (state, { payload }) => {
      state.couponData = payload;
    },
    setCouponSimplified: (
      state,
      { payload }: { payload: { code: string; data: CouponType } },
    ) => {
      state.coupon = payload.code;
      state.couponData = payload.data;
    },
    clearItems: (state) => {
      state.items = [];
      state.itemsDefault = [];
      state.hasMoreReviewPages = false;
      // Clear cursor when clearing items
      const companyId = state.selectedCompany.domain;
      if (companyId) {
        state.reviewsCursor[companyId] = undefined;
        state.hasMoreReviews[companyId] = true;
      }
    },
    setPriceTier: (state, { payload }: { payload: PriceTierType }) => {
      state.priceTier = payload;
      state.version = payload.version;
    },
    setAddressCompany: (state, { payload }) => {
      state.addressCompany = payload || "";
    },
    setSelectedCompany: (state, { payload }) => {
      state.selectedCompany = payload;
      state.hasMoreReviewPages = false;
      const companyId = payload.domain;
      if (companyId) {
        state.chosenCheckBoxRating =
          state.companyRatingFilters[companyId] || [];
        // Reset cursor when selecting a new company
        state.reviewsCursor[companyId] = undefined;
        state.hasMoreReviews[companyId] = true;
      } else {
        state.chosenCheckBoxRating = [];
      }
    },
    setForceSelectedCompanies: (state, { payload }) => {
      const firstCompany = payload?.[0];
      state.selectedCompanies = payload ?? [];
      state.hasMoreReviewPages = false;

      if (firstCompany) {
        const companyId = firstCompany.business_id;
        if (companyId) {
          state.chosenCheckBoxRating =
            state.companyRatingFilters[companyId] || [];
          state.reviewsCursor[companyId] = undefined;
          state.hasMoreReviews[companyId] = true;
        } else {
          state.chosenCheckBoxRating = [];
        }
        state.addressCompany =
          firstCompany.address || firstCompany.street_address || "";
      } else {
        // Reset when no companies provided
        state.selectedCompany = {} as TrustPilotBusinessType;
        state.chosenCheckBoxRating = [];
      }
    },
    setIsOpenModal: (state) => {
      state.isOpenModal = !state.isOpenModal;
    },
    setOneItem: (state, { payload }) => {
      state.items = [payload];
    },
    setSearchText: (state, { payload }) => {
      state.searchText = payload;
    },
    setSelectedItem: (state, { payload }) => {
      state.selectedItem = payload;
    },
    setItemsEmpty: (state) => {
      state.items = [];
    },
    /**
     * Toggles item selection with support for shift-click bulk selection.
     * When shift is held, selects all items between the last selected item
     * and the current item.
     */
    setToggleItem: (
      state,
      { payload }: { payload: { reviewId: string; isShiftUp: boolean } },
    ) => {
      const { reviewId, isShiftUp } = payload;
      if (!reviewId) return;

      const selectedReviewIndex = state.items.findIndex(
        (review) => review.id === reviewId,
      );
      const startingReviewIndex = state.items.findIndex(
        (review) => review.isChosenItem,
      );

      state.items = state.items.map((review, index) => {
        if (review.id === reviewId) {
          return { ...review, isChosenItem: !review.isChosenItem };
        }

        const selectedCondition =
          selectedReviewIndex > startingReviewIndex
            ? index <= selectedReviewIndex && index > startingReviewIndex
            : index >= selectedReviewIndex && index < startingReviewIndex;
        return isShiftUp && selectedCondition
          ? { ...review, isChosenItem: true }
          : review;
      });

      syncTrustPilotSelectedReviews(
        state,
        filterReviewsTrustPilot(state.selectedCompany, state.items) ?? [],
      );
      syncTrustPilotRatingCheckboxes(state);
    },
    setIsSuccessRequest: (state, { payload }) => {
      state.isSuccessRequest = payload;
    },
    setData: (
      state,
      {
        payload,
      }: { payload: { field: TrustPilotUserFormField; value: string } },
    ) => {
      const { field, value } = payload;
      (state.data as Record<string, string | boolean | undefined>)[field] =
        value;
    },
    setUserDataBulk: (
      state,
      { payload }: { payload: Partial<TrustPilotUserFormData> },
    ) => {
      state.data = { ...state.data, ...payload };
    },
    // This is to prevent the persistent cart side effect call
    setUserDataBulkNonPersisting: (
      state,
      { payload }: { payload: Partial<TrustPilotUserFormData> },
    ) => {
      state.data = { ...state.data, ...payload };
    },
    setFormsQuestion: (state, { payload }) => {
      const { field, value } = payload as { [key: string]: string };
      state.formsQuestion = { ...state.formsQuestion, [field]: value };
    },
    setLoading: (state, { payload }) => {
      state.isLoading = payload;
    },
    setNumberLastItem: (state, { payload }) => {
      state.numberLastItem = payload;
    },
    setCleanData: (state) => {
      state.formsQuestion = {};
    },
    setIsSendData: (state, { payload }) => {
      state.isSendData = payload;
    },
    clearError: (state) => {
      state.error = "";
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
    setPriceTierReviewCountLimit: (state, { payload }: { payload: number }) => {
      state.priceTierReviewCountLimit = payload;
    },
    setCartShare: (
      state,
      { payload }: { payload: TrustPilotCartShareData },
    ) => {
      state.cartShare = payload;
    },
    toggleCartShare: {
      reducer: (state, { payload }: { payload?: { visibility: boolean } }) => {
        if (payload?.visibility !== undefined) {
          state.isCartShareOpen = payload.visibility;
          return;
        }
        state.isCartShareOpen = !state.isCartShareOpen;
      },
      prepare: (payload?: { visibility: boolean }) => ({ payload }),
    },
    resetCoupon: (state) => {
      state.coupon = initialState.coupon;
      state.couponData = initialState.couponData;
    },
    setCleanAllData: (state) => {
      state.error = "";
      state.searchText = "";
      state.data = {} as TrustPilotUserFormData;
      state.formsQuestion = {} as { [key: string]: string };
      state.isLoading = true as boolean | null;
      state.isLoadingFetchGetCompany = true as boolean | null;
      state.numberLastItem = 0 as number;
      state.isSendData = false;
      state.isSuccessRequest = null as boolean | null;
      state.items = [] as TrustPilotReviewType[];
      state.itemsDefault = [] as TrustPilotReviewType[];
      state.selectedItem = 0;
      state.disableForm = false as boolean;
      state.countReview = 0;
      state.company = [] as SearchBusinessTrustPilotResultType[];
      state.limit = 9;
      state.limitCompany = 5;
      state.page = 0;
      state.isOpenModal = false;
      state.countAllReview = {} as { [key: string]: number };
      state.addressCompany = "";
      state.selectedCompany = {} as TrustPilotBusinessType; // current selected company
      state.selectedCompanies = [] as TrustPilotBusinessType[];
      state.coupon = "";
      state.couponData = {} as CouponType;
      state.reviewShowTextAreaId = [] as string[];
      state.chosenCheckBoxRating = [] as number[];
      state.companyRatingFilters = {} as { [key: string]: number[] };
      state.priceItem = 0;
      state.error = "" as string | undefined | null;
      state.isOpenCart = false;
      state.reviewsCursor = {} as { [key: string]: string | undefined }; // Cursor for each company
      state.hasMoreReviews = {} as { [key: string]: boolean }; // Track if more reviews exist
      state.companyCursor = undefined as string | undefined; // Cursor for company search,
      state.priceTier = undefined as PriceTierType | undefined;
      state.version = undefined as PriceTierVersion | undefined;
      state.priceTierReviewCountLimit = -1 as number;
      state.selectedReviewFilter = undefined as OptionsType | undefined;
      state.isCartShareOpen = false;
      state.cartShare = undefined as TrustPilotCartShareData | undefined;
      state.hasMoreReviewPages = false;
    },
    setPage: (state, { payload }: { payload: number }) => {
      state.page = payload;
    },
    clearCartContents: (state) => {
      state.items.forEach((it) => {
        it.isChosenItem = false;
      });

      state.selectedCompany.userSelectedReviews = [];
      state.selectedCompanies.forEach((it) => {
        it.userSelectedReviews = [];
      });

      state.coupon = "";
      state.couponData = {};
    },
    removeAllSelectedReviewsByCompany: (
      state,
      { payload }: { payload: { targetBusiness: TrustPilotBusinessType } },
    ) => {
      const targetBusiness = payload.targetBusiness;
      const business = state.selectedCompanies.find(
        (it) => it.domain === targetBusiness.domain,
      );

      if (!business) {
        return;
      }

      if ((business?.userSelectedReviews?.length ?? 0) > 0) {
        business.userSelectedReviews = [];
      }

      if (state.selectedCompany.domain === business.domain) {
        state.selectedCompany.userSelectedReviews = [];
        state.items.forEach((review) => {
          review.isChosenItem = false;
        });
      }
    },
    removeReviewByCompany: (
      state,
      {
        payload,
      }: {
        payload: {
          review: TrustPilotReviewType;
          company: TrustPilotBusinessType;
        };
      },
    ) => {
      const review = payload.review;
      const company = payload.company;
      const targetSelectedCompany = state.selectedCompanies.find(
        (it) => it.domain === company.domain,
      );

      if (
        targetSelectedCompany &&
        (targetSelectedCompany?.userSelectedReviews?.length ?? 0) > 0
      ) {
        targetSelectedCompany.userSelectedReviews =
          targetSelectedCompany?.userSelectedReviews?.filter((it) => {
            return it.id !== review.id;
          });
      }

      if (state.selectedCompany.domain === company.domain) {
        state.selectedCompany.userSelectedReviews = (
          state.selectedCompany.userSelectedReviews ?? []
        ).filter((it) => it.id !== review.id);
        state.items.forEach((it) => {
          if (it.id === review.id) {
            it.isChosenItem = false;
          }
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReviewStateTrustPilot, () => {
      return initialState;
    });
    builder.addCase(fetchGetReviewsTrustPilot.pending, (state, action) => {
      state.isLoading = true;
      state.disableForm = true;
      state.error = "";
      state.hasMoreReviewPages = false;
    });
    builder.addCase(
      fetchGetReviewsTrustPilot.fulfilled,
      (state, { payload }) => {
        const {
          dataArray,
          fetchedCompany,
          selectedReviews,
          pagination,
          cartShareInvalid,
        } = payload;
        const reviews = dedupeTrustPilotReviewsById(dataArray);
        const selected = dedupeTrustPilotReviewsById(selectedReviews);

        state.items = reviews;
        state.itemsDefault = reviews;
        state.pagination = pagination;
        state.hasMoreReviewPages =
          reviews.length > 0 &&
          Number(pagination.current_page) < Number(pagination.total_pages);
        state.isLoading = null;
        state.page = Number(pagination.current_page) || 1;

        const business: TrustPilotBusinessType = {
          ...fetchedCompany,
          userSelectedReviews: selected,
        };

        state.countReviewByRating = countTrustPilotReviewsByRating(reviews);
        state.disableForm = cartShareInvalid;
        state.error = cartShareInvalid
          ? TRUSTPILOT_INVALID_CART_SHARE_MESSAGE
          : "";
        state.selectedCompany = business;
        state.selectedCompanies = [business];
      },
    );
    builder.addCase(
      fetchGetReviewsTrustPilot.rejected,
      (state, { payload }) => {
        state.isLoading = false;
        state.disableForm = false;
        state.error = (payload as string) || TRUSTPILOT_GENERIC_ERROR_MESSAGE;
      },
    );
    builder.addCase(fetchCreateReviewTrustPilot.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(
      fetchCreateReviewTrustPilot.fulfilled,
      (state, { payload }) => {
        state.isLoading = false;
      },
    );
    builder.addCase(fetchCreateReviewTrustPilot.rejected, (state, action) => {
      state.isLoading = false;
      // The duplicate sentinel is control flow, not an error: writing it to
      // state.error would render "order_submission_in_progress" to customers.
      if (!isDuplicateOrderSubmission(action.payload)) {
        state.error = action.payload as string;
      }
    });
    builder.addCase(fetchGetCompanyTrustPilot.pending, (state, action) => {
      state.isLoadingFetchGetCompany = true;
      state.error = "";
    });
    builder.addCase(
      fetchGetCompanyTrustPilot.fulfilled,
      (state, { payload }) => {
        const { data, countAllReview } = payload;
        state.company = data.businessUnits ?? [];
        state.isLoadingFetchGetCompany = null;
        state.countAllReview = countAllReview;
        state.items = [];
      },
    );
    builder.addCase(fetchGetCompanyTrustPilot.rejected, (state, action) => {
      state.isLoadingFetchGetCompany = false;
      state.company = [];
      state.error =
        (action.payload as string) || TRUSTPILOT_GENERIC_ERROR_MESSAGE;
    });
    builder.addCase(
      fetchCheckCouponTrustPilot.fulfilled,
      (state, { payload }) => {
        state.couponData = payload;
      },
    );
    builder.addCase(fetchCheckCouponTrustPilot.rejected, (state, action) => {
      state.coupon = "";
      state.couponData = {
        error: action.payload,
      } as CouponType;
    });

    builder.addCase(fetchGetReviewsMoreTrustPilot.pending, (state, action) => {
      state.fetchGetReviewsMoreTrustPilot = true;
      state.error = "";
    });
    builder.addCase(
      fetchGetReviewsMoreTrustPilot.fulfilled,
      (state, { payload }) => {
        state.fetchGetReviewsMoreTrustPilot = false;
        if (payload.companyId !== state.selectedCompany.domain) return;

        const pageReviews = dedupeTrustPilotReviewsById(payload.reviews);
        const selectedIds = new Set(
          (state.selectedCompany.userSelectedReviews ?? [])
            .map((review) => review.id)
            .filter(Boolean),
        );
        const items = dedupeTrustPilotReviewsById([
          ...state.items,
          ...pageReviews,
        ]).map((review) =>
          selectedIds.has(review.id)
            ? { ...review, isChosenItem: true }
            : review,
        );

        state.items = items;
        state.itemsDefault = items;
        state.page =
          Number(payload.pagination.current_page) || payload.requestedPage;
        state.pagination = payload.pagination;
        state.hasMoreReviewPages =
          pageReviews.length > 0 &&
          Number(payload.pagination.current_page) >= payload.requestedPage &&
          Number(payload.pagination.current_page) <
            Number(payload.pagination.total_pages);
        state.countReviewByRating = countTrustPilotReviewsByRating(items);
      },
    );
    builder.addCase(fetchGetReviewsMoreTrustPilot.rejected, (state, action) => {
      state.fetchGetReviewsMoreTrustPilot = false;
      state.error =
        (action.payload as string) || TRUSTPILOT_GENERIC_ERROR_MESSAGE;
    });
  },
});

export const selectChosenCheckBoxRatingTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.chosenCheckBoxRating;

export const selectCouponTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.coupon;

export const selectCouponDataTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.couponData;

export const selectCompanyTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.company;

export const selectSelectedCompanyTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.selectedCompany;

// Add filter for only show the companies with selected reviews
export const selectSelectedCompaniesTrustPilot = createSelector(
  (state: RootState) => state.reviewTrustPilotReducer.selectedCompanies,
  (selectedCompanies) =>
    filterSelectedCompaniesWithReviewsTrustPilot(selectedCompanies),
);

export const selectSelectedCompaniesRawTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.selectedCompanies;

export const selectCompanyRatingFiltersTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.companyRatingFilters;

export const selectItemsTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.items;

export const selectPriceTierTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.priceTier;
export const selectPriceTierVersionTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.version;
export const selectIsLoadingTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.isLoading;
export const selectDisableFormTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.disableForm;
export const selectDataTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.data;

export const selectPriceItemTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.priceItem;

export const selectIsOpenCartTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.isOpenCart;

export const selectIsLoadingFetchGetCompanyTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.isLoadingFetchGetCompany;

export const selectPriceTierReviewCountLimitTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.priceTierReviewCountLimit;

export const selectCountReviewByRatingTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.countReviewByRating;

export const selectPaginationTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.pagination;
export const selectHasMoreReviewPagesTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.hasMoreReviewPages;
export const selectPageTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.page;

export const selectErrorTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.error;

export const selectSelectedReviewFilterTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.selectedReviewFilter;

export const selectPersistentCartPayloadTrustPilot = (
  state: RootState,
): PersistedCartPayloadTrustPilot => {
  const company = selectSelectedCompanyTrustPilot(state);
  const businessPayload: TrustPilotPersistedBusinessPayload[] = [];

  if ((company?.userSelectedReviews?.length ?? 0) > 0) {
    const { userSelectedReviews, ...business } = company;
    businessPayload.push({
      ...business,
      reviews: userSelectedReviews,
    });
  }

  return {
    payload: {
      businesses: businessPayload,
      coupon_code: state.reviewTrustPilotReducer.coupon,
      coupon_data: state.reviewTrustPilotReducer.couponData,
      user: state.reviewTrustPilotReducer.data,
    },
  };
};

export const selectFetchGetReviewsMoreTrustPilot = (state: RootState) =>
  state.reviewTrustPilotReducer.fetchGetReviewsMoreTrustPilot;

export const {
  reducer: reviewTrustPilotReducer,
  actions: reviewTrustPilotActions,
} = TrustPilotReviewSlice;
